import {
  converter,
  formatHex,
  formatHex8,
  modeHsl,
  modeLab65,
  modeLrgb,
  modeOklch,
  modeRgb,
  parse,
  useMode,
  wcagLuminance,
} from "culori/fn";

import { ColorHex } from "~/shared/enums";

import { simpleIntHash } from "./common";

useMode(modeRgb);
useMode(modeOklch);
useMode(modeHsl);
useMode(modeLab65);
useMode(modeLrgb);

const toHsl = converter("hsl");

export const WHITE_HEX = "#ffffff";
export const BLACK_HEX = "#000000";

export const BACKGROUND_ALPHA = "4D";
export const DEFAULT_HIGHLIGHT_COLOR = ColorHex.PURE_YELLOW;

const isSetColorValue = (color: string) => color.trim().length > 0 && color !== "default";

export const isSetRecentColor = (entry: { color: string }) => isSetColorValue(entry.color);

const parseColor = (color: string) => {
  const parsed = parse(color);
  if (!parsed) {
    throw new Error(`Unsupported CSS color: ${color}`);
  }
  return parsed;
};

export const toHex = (color: string): string => {
  const parsed = parseColor(color);
  return parsed.alpha === undefined || parsed.alpha === 1 ? formatHex(parsed) : formatHex8(parsed);
};

export const toHexWithAlpha = (color: string, alpha?: number): string =>
  formatHex8({ ...parseColor(color), ...(alpha === undefined ? {} : { alpha }) });

export const withCssAlpha = (color: string, alpha: number): string =>
  `color-mix(in oklab, ${color} ${alpha * 100}%, transparent)`;

export const getChipColorsHex = (
  colorHex: string,
  theme: "light" | "dark"
): { back: string; fore: string; border: string } => {
  const color = parseColor(colorHex);
  const baseColor = formatHex({ ...color, alpha: 1 });
  if (theme === "light") {
    const fore = wcagLuminance(color) > 0.5 ? BLACK_HEX : WHITE_HEX;
    return { back: baseColor, fore, border: baseColor };
  }
  const hslColor = toHsl(color);
  hslColor.l = Math.max(hslColor.l, 0.8);
  const fore = formatHex(hslColor);
  return { back: formatHex8({ ...color, alpha: 0.2 }), fore, border: baseColor };
};

const generateColorFromValues = (v1: number, v2: number, v3: number): string =>
  formatHex({
    mode: "lab65",
    l: v1 * 40 + 30,
    a: v2 * 256 - 128,
    b: v3 * 256 - 128,
  });

export const makeRandomColorHex = (): string => generateColorFromValues(Math.random(), Math.random(), Math.random());

export const hashToColor = (str: string): string => {
  const absHash = simpleIntHash(str);
  const v1 = (absHash % 256) / 255;
  const v2 = (Math.floor(absHash / 256) % 256) / 255;
  const v3 = (Math.floor(absHash / 65536) % 256) / 255;
  return generateColorFromValues(v1, v2, v3);
};
